// Column widths for the shell — the joint clamp, the two splitters, and the
// preference that survives a reload.
//
// COPIED FROM main.ts, deliberately, and the reasoning is in shell.ts: the fleet
// console runs this logic every day and its copy is not edited here. What is not
// copied is the fleet's `activeMap` module global — the centre tells itself its
// box changed through an `onResize` callback, because a client whose centre is
// not a MapLibre map still has to be notified.
//
// THE STORAGE KEYS ARE NAMESPACED PER CONSOLE. Sharing one key across consoles
// would let a fleet operator's saved sidebar width land on a thermal client's
// layout, where the natural widths are different — and worse, it would let this
// new console write a preference that TDI-Brooks then reads. Namespacing is what
// keeps "we did not touch the fleet console" true at runtime as well as in the
// file tree.
//
// The floors below must match the minmax() floor on the map track in style.css.
// If that stylesheet changes, this file is the other half of the change.

const COL2_MIN_W = 320;
const WSPANEL_MIN_W = 240;
const MAP_MIN_W = 280;
const GUTTER_W = 7; // each splitter track in the .appbody template
const RAIL_W = 74;

function ns(body: HTMLElement): string {
  return body.dataset["consoleNs"] ?? "console";
}

function col2Key(body: HTMLElement): string {
  return `seastate_${ns(body)}_col2_w_v1`;
}

function wsKey(body: HTMLElement): string {
  return `seastate_${ns(body)}_wspanel_w_v1`;
}

function savedWidth(key: string): number | null {
  try {
    const v = Number(window.localStorage.getItem(key));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null; // storage blocked — resizing still works for the session
  }
}

function store(key: string, w: number): void {
  try {
    window.localStorage.setItem(key, String(Math.round(w)));
  } catch {
    /* storage blocked — the width still holds for this session */
  }
}

function railWidth(body: HTMLElement): number {
  const rail = body.querySelector(".rail");
  return rail instanceof HTMLElement ? rail.getBoundingClientRect().width : RAIL_W;
}

/** The workspace panel's width AS A GRID TRACK — 0 when closed or floating.
 *  Between 881 and 1180 px the panel is positioned absolutely and floats over
 *  the centre, so it occupies no track and the centre must not pay for it. */
function wsTrackWidth(body: HTMLElement): number {
  const panel = body.querySelector(".wspanel");
  if (!(panel instanceof HTMLElement) || panel.hidden) return 0;
  if (window.getComputedStyle(panel).position === "absolute") return 0;
  return panel.getBoundingClientRect().width;
}

function clampCol2Width(body: HTMLElement, w: number): number {
  const wsW = wsTrackWidth(body);
  const gutters = (wsW > 0 ? 2 : 1) * GUTTER_W;
  const max = window.innerWidth - railWidth(body) - wsW - gutters - MAP_MIN_W;
  return Math.max(COL2_MIN_W, Math.min(max, w));
}

/**
 * One layout pass over both column variables.
 *
 * Re-derives each from its saved preference (or the CSS default), jointly
 * clamped so the centre track keeps MAP_MIN_W: the track stack gives up width
 * first, the workspace panel second. Runs at mount, on panel toggle and on
 * window resize, so a layout saved on a wide monitor and restored into a
 * half-width window degrades by narrowing columns rather than by collapsing the
 * centre to nothing.
 */
export function reclampColumns(body: HTMLElement): void {
  if (body.classList.contains("col2closed")) return; // folded by decision, not squeeze
  const gutter = body.querySelector(".gutter:not(.lgutter)");
  // Single-column stack (≤880 px): the columns are not tracks at all.
  if (!(gutter instanceof HTMLElement)) return;
  if (window.getComputedStyle(gutter).display === "none") return;

  const vw = window.innerWidth;
  const wsInFlow = wsTrackWidth(body) > 0;
  const savedCol2 = savedWidth(col2Key(body));
  const savedWs = savedWidth(wsKey(body));
  // Fallbacks mirror the CSS defaults: --col2-w clamp(380px, 34vw, 560px),
  // --wspanel-w 300px.
  let col2 = Math.max(COL2_MIN_W, savedCol2 ?? Math.min(560, Math.max(380, vw * 0.34)));
  let ws = wsInFlow
    ? Math.max(WSPANEL_MIN_W, Math.min(savedWs ?? 300, Math.floor(vw * 0.4)))
    : 0;
  let over =
    railWidth(body) + ws + (wsInFlow ? 2 : 1) * GUTTER_W + MAP_MIN_W + col2 - vw;
  const squeezed = over > 0;
  if (over > 0) {
    const give = Math.min(over, col2 - COL2_MIN_W);
    col2 -= give;
    over -= give;
  }
  if (over > 0 && wsInFlow) ws = Math.max(WSPANEL_MIN_W, ws - over);

  // Pin a pixel width only where something demands it — a saved preference or an
  // active squeeze. Otherwise clear it so the CSS defaults stay responsive.
  if (savedCol2 !== null || squeezed) {
    body.style.setProperty("--col2-w", `${Math.round(col2)}px`);
  } else {
    body.style.removeProperty("--col2-w");
  }
  if (wsInFlow) {
    if (savedWs !== null || squeezed) {
      body.style.setProperty("--wspanel-w", `${Math.round(ws)}px`);
    } else {
      body.style.removeProperty("--wspanel-w");
    }
  }
}

/** Wire one splitter. `measure` turns a pointer x into a desired width. */
function drag(
  body: HTMLElement,
  grip: HTMLElement,
  measure: (x: number, rect: DOMRect) => number,
  apply: (w: number) => void,
  persist: (w: number) => void,
  onResize?: () => void,
): () => void {
  let last = 0;
  const down = (e: PointerEvent): void => {
    e.preventDefault();
    grip.classList.add("dragging");
    grip.setPointerCapture(e.pointerId);
  };
  const move = (e: PointerEvent): void => {
    if (!grip.hasPointerCapture(e.pointerId)) return;
    // Folded away, the splitter is only a handle. Dragging a column that is not
    // there would set a width that appears the moment it comes back, silently.
    if (body.classList.contains("col2closed")) return;
    last = measure(e.clientX, body.getBoundingClientRect());
    apply(last);
    onResize?.();
  };
  const end = (e: PointerEvent): void => {
    if (!grip.hasPointerCapture(e.pointerId)) return;
    grip.releasePointerCapture(e.pointerId);
    grip.classList.remove("dragging");
    if (last > 0) persist(last);
    onResize?.();
  };
  grip.addEventListener("pointerdown", down);
  grip.addEventListener("pointermove", move);
  grip.addEventListener("pointerup", end);
  grip.addEventListener("pointercancel", end);
  return () => {
    grip.removeEventListener("pointerdown", down);
    grip.removeEventListener("pointermove", move);
    grip.removeEventListener("pointerup", end);
    grip.removeEventListener("pointercancel", end);
  };
}

/**
 * Wire both splitters and keep the columns clamped against the viewport.
 * Returns a teardown that removes every listener it added.
 */
export function setupColumns(
  body: HTMLElement,
  gutter: HTMLElement,
  lgutter: HTMLElement,
  onCloseLeft: () => void,
  onResize?: () => void,
): () => void {
  const offs: Array<() => void> = [];

  // Right splitter: drags the track stack, measured from the body's right edge.
  offs.push(
    drag(
      body,
      gutter,
      (x, rect) => clampCol2Width(body, rect.right - x),
      (w) => body.style.setProperty("--col2-w", `${w}px`),
      (w) => store(col2Key(body), w),
      onResize,
    ),
  );

  // Left splitter: drags the workspace panel, measured from the rail's edge.
  offs.push(
    drag(
      body,
      lgutter,
      (x, rect) => {
        const want = x - rect.left - railWidth(body);
        const max = Math.floor(window.innerWidth * 0.4);
        return Math.max(WSPANEL_MIN_W, Math.min(max, want));
      },
      (w) => body.style.setProperty("--wspanel-w", `${w}px`),
      (w) => store(wsKey(body), w),
      onResize,
    ),
  );

  // Double-click either splitter: back to the CSS default.
  const resetRight = (): void => {
    try {
      window.localStorage.removeItem(col2Key(body));
    } catch {
      /* storage blocked */
    }
    body.style.removeProperty("--col2-w");
    reclampColumns(body);
    onResize?.();
  };
  const resetLeft = (): void => {
    try {
      window.localStorage.removeItem(wsKey(body));
    } catch {
      /* storage blocked */
    }
    body.style.removeProperty("--wspanel-w");
    reclampColumns(body);
    onResize?.();
  };
  gutter.addEventListener("dblclick", resetRight);
  lgutter.addEventListener("dblclick", resetLeft);
  offs.push(() => gutter.removeEventListener("dblclick", resetRight));
  offs.push(() => lgutter.removeEventListener("dblclick", resetLeft));

  // Dragging the left splitter to its floor closes the panel outright, which is
  // what the operator meant by dragging it shut.
  const maybeClose = (): void => {
    if (wsTrackWidth(body) > 0 && wsTrackWidth(body) <= WSPANEL_MIN_W + 2) onCloseLeft();
  };
  lgutter.addEventListener("pointerup", maybeClose);
  offs.push(() => lgutter.removeEventListener("pointerup", maybeClose));

  const onWinResize = (): void => {
    reclampColumns(body);
    onResize?.();
  };
  window.addEventListener("resize", onWinResize);
  offs.push(() => window.removeEventListener("resize", onWinResize));

  reclampColumns(body); // restore the saved layout, clamped to this viewport
  return () => {
    for (const off of offs) off();
  };
}
